/**
 * Local mirror of the instance-employee `config` shape for the
 * provisioning "constructor" form. The admin-ui does not depend on
 * @nbts/shared, so these mirror the authoritative Zod source of truth in
 * shared/schemas/instance-employee-sync.ts + shared/schemas/employee-config-blocks.ts.
 * Keep in sync with those.
 */

// ── Config blocks (rendered as cards on the instance employee editor) ────────

export type ConfigBlockFieldType =
  | "text"
  | "textarea"
  | "number"
  | "boolean"
  | "select";

export type ConfigBlockValue = string | number | boolean | null;

export interface ConfigBlockFieldOption {
  value: string;
  label: string;
}

export interface ConfigBlockField {
  key: string;
  label: string;
  description?: string;
  type: ConfigBlockFieldType;
  options?: ConfigBlockFieldOption[];
  required?: boolean;
  placeholder?: string;
  default?: ConfigBlockValue;
}

export interface ConfigBlock {
  id: string;
  icon?: string;
  title: string;
  description?: string;
  fields: ConfigBlockField[];
  /**
   * `false` = operator-owned: the tenant editor hides the block and the
   * instance guard rejects tenant value writes; field `default`s act as
   * the operator-set values injected into the prompt. Absent/`true` =
   * tenant-editable.
   */
  tenantEditable?: boolean;
}

export interface RuntimeModelPolicy {
  provider?: string;
  model?: string;
  temperature?: number;
  max_output_tokens?: number;
  fallback_models?: string[];
}

export type FirstMessageMode =
  | "assistant-speaks-first"
  | "assistant-waits-for-user"
  | "assistant-speaks-first-with-model-generated-message";

/**
 * Operator-managed voice-engine settings for the employee's phone
 * assistant. All fields optional — absent fields keep the engine
 * defaults. Never exposed to the tenant. Restricted to this allowlist:
 * `voice`/`transcriber` are intentionally not operator-configurable.
 */
export interface VoiceSettings {
  firstMessageMode?: FirstMessageMode;
  /** 10..7200 */
  maxDurationSeconds?: number;
  backgroundSound?: "off" | "office";
  /** 10..600 */
  silenceTimeoutSeconds?: number;
  /** Max 500 chars. */
  endCallMessage?: string;
  endCallFunctionEnabled?: boolean;
  /** Raw JSON merged LAST into the voice-engine assistant configuration. */
  advancedOverridesJson?: string;
  /**
   * Assistant sections tuned in the Vapi dashboard: set by the backend on
   * create only, preserved on later syncs ("model" still gets the system
   * prompt + tools). Absent = backend owns everything.
   */
  vapiOwnedFields?: Array<"model" | "transcriber" | "voice">;
}

export interface InitialDefaults {
  greeting?: string;
  /** Seconds of silence before the voice greeting (0–10, voice channels only). */
  voiceGreetingDelaySeconds?: number;
  languages?: string[];
  emailGreeting?: string;
  emailSignature?: string;
}

/** The known keys the constructor manages. Unknown keys are preserved. */
export interface InstanceEmployeeConfig {
  tone?: string;
  description?: string;
  mission?: string;
  valueProposition?: string;
  expertise?: string[];
  qualificationMethod?: string;
  salesMethodology?: string;
  workflows?: Record<string, string[]>;
  customRules?: Array<{ title: string; body: string }>;
  toolIds?: string[];
  supportedMessageTypes?: string[];
  emailReplyMode?: "draft" | "auto_send" | "manual";
  runtimeModelPolicy?: RuntimeModelPolicy;
  initialDefaults?: InitialDefaults;
  /** Operator-only voice-engine settings for the phone assistant. */
  voiceSettings?: VoiceSettings;
  /**
   * Manual system-prompt mode. When true, `manualSystemPrompt` is shipped to
   * the voice assistant / chat handler VERBATIM and the LLM prompt generator
   * is disabled for this employee. false/undefined → generated.
   */
  useManualPrompt?: boolean;
  /** Author-supplied final system prompt, shipped verbatim in manual mode. */
  manualSystemPrompt?: string | null;
  blocks?: ConfigBlock[];
  // ── email_responder-specific settings ──
  /** Let the bot read inbound email attachments. */
  readAttachments?: boolean;
  /** Pool of human employees the bot hands a session off to on escalation. */
  escalationTargets?: {
    employeeIds?: string[];
    mode?: "round_robin" | "least_load";
  };
  /**
   * Field keys the operator has chosen to expose to the tenant. The sync
   * maps these onto the employee's customization policy (tenant tier), so
   * the tenant can see and edit exactly these fields in their own editor.
   */
  tenantEditableFields?: string[];
  /**
   * Integration IDs the operator selected in the "Connect tools and
   * communication channels" block. The sync applies them as the
   * employee↔integration binding on the instance (seed-once).
   */
  channelIntegrationIds?: string[];
  [key: string]: unknown;
}

/** The slim integration record the instance-integrations proxy returns. */
export interface InstanceIntegration {
  id: string;
  name: string;
  type: string;
  provider: string;
  status: string;
}

/** Config keys the constructor renders an editor for (rest is preserved). */
export const MANAGED_CONFIG_KEYS: readonly string[] = [
  "tone",
  "description",
  "mission",
  "valueProposition",
  "expertise",
  "qualificationMethod",
  "salesMethodology",
  "workflows",
  "customRules",
  "toolIds",
  "supportedMessageTypes",
  "emailReplyMode",
  "runtimeModelPolicy",
  "initialDefaults",
  "voiceSettings",
  "useManualPrompt",
  "manualSystemPrompt",
  "blocks",
  "readAttachments",
  "escalationTargets",
  "tenantEditableFields",
  "channelIntegrationIds",
];

/**
 * The tenant-facing field key each constructor control maps to. These are
 * the names the customization policy + tenant editor understand (employee
 * entity fields), NOT the raw config JSON keys — the sync bridges the two.
 * `languages` lives in initialDefaults; block values are `configBlockValues`.
 */
export const EXPOSABLE_FIELD_KEYS = {
  greeting: "greeting",
  voiceGreetingDelay: "voiceGreetingDelaySeconds",
  languages: "languages",
  channels: "channels",
  emailGreeting: "emailGreeting",
  emailSignature: "emailSignature",
  emailReplyMode: "emailReplyMode",
  escalationTargets: "escalationTargets",
  readAttachments: "readAttachments",
  workingHours: "workingHours",
  kpis: "kpis",
  configBlockValues: "configBlockValues",
  toolIds: "toolIds",
  supportedMessageTypes: "supportedMessageTypes",
  runtimeModelPolicy: "runtimeModelPolicy",
  workflows: "workflows",
  customRules: "customRules",
} as const;

/**
 * The fields the tenant employee editor can render in central mode, in
 * display order. The constructor's "Visible in tenant UI" card exposes
 * exactly these via config.tenantEditableFields; under strict visibility a
 * field is shown to the tenant ONLY when its key is checked here.
 */
export const TENANT_VISIBLE_FIELDS: readonly { key: string; label: string }[] = [
  { key: "greeting", label: "Greeting" },
  { key: "languages", label: "Languages" },
  { key: "channels", label: "Channels & integrations" },
  { key: "emailGreeting", label: "Email greeting" },
  { key: "emailSignature", label: "Email signature" },
  { key: "emailReplyMode", label: "Email reply mode" },
  { key: "escalationTargets", label: "Escalation team" },
  { key: "readAttachments", label: "Read attachments" },
  { key: "workingHours", label: "Working hours" },
  { key: "kpis", label: "KPIs / metrics" },
  { key: "configBlockValues", label: "Config block values" },
];

// ── Option lists for the dropdowns ───────────────────────────────────────────

/** The five user-meaningful inbound message types (shared enum has 10). */
export const MESSAGE_TYPES: readonly string[] = [
  "text",
  "image",
  "audio",
  "video",
  "document",
];

/** Supported locales (mirror of frontend i18n supportedLngs). */
export const LANGUAGES: readonly { value: string; label: string }[] = [
  { value: "en", label: "English" },
  { value: "de", label: "Deutsch" },
  { value: "ru", label: "Русский" },
  { value: "uk", label: "Українська" },
  { value: "es", label: "Español" },
  { value: "tr", label: "Türkçe" },
];

/** Communication tone options (mirror of the instance CommunicationTone). */
export const TONE_OPTIONS: readonly string[] = [
  "friendly",
  "professional",
  "formal",
  "consultative",
];

export const FIELD_TYPES: readonly ConfigBlockFieldType[] = [
  "text",
  "textarea",
  "number",
  "boolean",
  "select",
];

export const EMAIL_REPLY_MODES: readonly string[] = ["draft", "auto_send"];

/**
 * Icon names the instance renderer supports (must match ICON_MAP in
 * frontend/.../dynamic-config-blocks-section.tsx). Unknown names fall back
 * to a gear on the instance, so we constrain the picker to these.
 */
export const BLOCK_ICONS: readonly string[] = [
  "BarChart3",
  "Bot",
  "Brain",
  "Clock",
  "FileText",
  "Globe",
  "Heart",
  "Mail",
  "MessageSquare",
  "Radio",
  "Settings2",
  "Shield",
  "Sliders",
  "Sparkles",
  "Star",
  "Users",
  "Zap",
];
